import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { SseExpertEvent } from '@/types/api';
import { openStream, type StreamHandle } from '@/lib/sse';
import { qk } from '@/lib/query-keys';
import { reportIssue } from '@/lib/telemetry';
import { useStreamStore } from '@/stores/stream-store';

/* ---------------------------------------------------------------------------
   The expert run stream, wired to the stream store.

   THE HANDSHAKE THAT MAKES A PAGE LOAD GAPLESS, and the reason this hook takes
   a cursor rather than opening blind:

     1. `GET /expert/chats/{id}` returns the thread AND `cursor.stream_cursor`.
     2. We open the stream with that cursor as `Last-Event-ID`.
     3. The server replays everything after it, then goes live.

   Without step 1 handing over a cursor, everything emitted between the fetch
   completing and the socket opening is lost — which on a live run is exactly the
   frames a reviewer is watching for.

   `stream_reset` is the other half. When the server cannot replay from our
   cursor it says so rather than silently starting from somewhere: the transport
   reports it, the store drops its stream-derived state and bumps a refetch
   token, and this hook re-fetches the chat so the ledger is rebuilt from the
   document instead of from a hole we cannot see the size of.

   ONE THING NOT TO GET WRONG. The cursor is dense within the EXPERT visibility
   projection. An expert cursor and a client cursor count different events, so
   one must never be replayed against the other. This hook only ever opens
   `/expert/chats/{id}/stream`.
--------------------------------------------------------------------------- */

export function useExpertStream(chatId: number | null, initialCursor: string | null | undefined) {
  const queryClient = useQueryClient();
  const handleRef = React.useRef<StreamHandle | null>(null);

  const ingest = useStreamStore((s) => s.ingest);
  const setConnection = useStreamStore((s) => s.setConnection);
  const hardReset = useStreamStore((s) => s.hardReset);

  // The newest cursor is kept in a ref so the socket is not re-opened every time
  // it moves — which, on a busy run, would be several times a second.
  const cursorRef = React.useRef<string | null>(initialCursor ?? null);
  if (cursorRef.current === null && initialCursor) cursorRef.current = initialCursor;

  React.useEffect(() => {
    if (chatId === null) return;

    setConnection('connecting');

    const handle = openStream<SseExpertEvent>(`/expert/chats/${chatId}/stream`, {
      initialCursor: cursorRef.current,
      onEvent: (event, frame) => {
        cursorRef.current = frame.id ?? cursorRef.current;
        ingest(event, frame.id);
      },
      onOpen: (attempt) => setConnection('open', attempt),
      onClose: ({ fatal }) => setConnection(fatal ? 'closed' : 'reconnecting'),
      onReset: (fromCursor, reason) => {
        cursorRef.current = fromCursor || null;
        hardReset();
        reportIssue({ kind: 'unknown_stream_event', token: `stream_reset:${reason}` });
        void queryClient.invalidateQueries({ queryKey: qk.chat(chatId) as unknown as unknown[] });
      },
      onMalformedFrame: (frame) => {
        reportIssue({ kind: 'render_error', token: 'malformed_sse_frame', context: { event: frame.event } });
      },
    });

    handleRef.current = handle;
    return () => {
      handle.close();
      handleRef.current = null;
    };
  }, [chatId, ingest, setConnection, hardReset, queryClient]);

  return handleRef;
}
